import React, { createContext, useContext, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { useDomainFetch } from './hooks/useDomainFetch';
import { useAuth } from './AuthContext';
import { useSettings } from './SettingsContext';
import logger from '../utils/logger';
import { Device, DeviceStatus, PendingReviewDevice, RemoteItStatus } from '../types';
import {
  isModberryDevice,
  isNH3Sensor,
  isBarnThermometer,
  isCellarDepthLaser,
  shouldCorrelateDevice,
} from '../utils/deviceTypeHelpers';
import { extractDeviceUUID } from '../utils/deviceNameUtils';
import type {
  FlowTelemetryResponse,
  NH3TelemetryResponse,
} from '../types/telemetry';
import { API_BASE_URL } from '../config/api';

// Device IDs explicitly excluded from production metrics
// These are dev/deprecated devices that should not appear in dashboard statistics
const EXCLUDED_DEVICE_IDS = new Set([
  '38b0ca80-e851-11ef-95da-514f24a3ef78', // Brunink#1 - DEV instance
  '2a86b600-b039-11ee-b45b-ebfecced3ed2', // Dairy Campus - not production
  '2d5da3c0-b039-11ee-b45b-ebfecced3ed2', // Vetketel - deprecated
]);

// Flow degradation timeline type -- matches API response.
// Previously defined in DevicesProvider.tsx; moved here for consolidation.
export interface FlowDegradationTimeline {
  deviceId: string;
  daysAtZero: number;
  daysAtCritical: number;
  daysFromHealthyToCritical: number | null;
  firstZeroDate: string | null;
  firstCriticalDate: string | null;
  lastHealthyDate: string | null;
  currentFlowRate: number | null;
  degradationRate: number | null;
  warningThreshold: number;
  criticalThreshold: number;
  summary: string;
}

interface DeviceContextType {
  // Sub-domain data
  devices: Device[] | null;
  remoteIt: RemoteItStatus | null;
  pendingReviewDevices: PendingReviewDevice[] | null;

  // Aggregate loading/error state (true if any sub-domain is loading/errored)
  loading: boolean;
  error: string | null;
  isStale: boolean;
  lastFetch: number | null;

  // Actions
  refetch: (force?: boolean) => Promise<void>;

  // Domain utilities (ported from DevicesProvider.tsx)
  getDeviceById: (deviceId: string) => Device | null;
  getDevicesByFarm: (farmName: string) => Device[];
  getActiveDevices: () => Device[];
  getInactiveDevices: () => Device[];
  getDevicesNeedingAttention: () => Device[];
  getExperimentalDevices: () => Device[];
  getProductionDevices: () => Device[];
  getFleetStatistics: () => {
    total: number;
    active: number;
    inactive: number;
    needingAttention: number;
    averageHealthScore: number;
  };
  getDevicesByHealthScore: (minScore: number) => Device[];
  triggerDiscovery: () => Promise<boolean>;
  forceRefreshDevice: (deviceId: string) => Promise<boolean>;
  fetchDeviceStatus: (deviceId: string) => Promise<DeviceStatus>;
  fetchFlowDegradationTimeline: (deviceId: string) => Promise<FlowDegradationTimeline | null>;
}

export const DeviceContext = createContext<DeviceContextType | undefined>(undefined);
export type { DeviceContextType };

interface DeviceProviderProps {
  children: ReactNode;
}

export function DeviceProvider({ children }: DeviceProviderProps) {
  const { refreshToken, logout } = useAuth();
  const { settings } = useSettings();

  const getToken = useCallback(
    () => (typeof window !== 'undefined' ? localStorage.getItem('tb_token') : null),
    [],
  );

  // Build onUnauthorized callback for 401 auto-refresh (matches authenticatedFetch pattern
  // from DevicesProvider.tsx lines 78-106).
  const onUnauthorized = useCallback(
    async (makeRequest: () => Promise<Response>): Promise<Response> => {
      const refreshTokenValue =
        typeof window !== 'undefined' ? localStorage.getItem('tb_refresh_token') : null;
      if (refreshTokenValue) {
        const refreshSuccess = await refreshToken();
        if (refreshSuccess) {
          return makeRequest();
        } else {
          logout();
        }
      } else {
        logout();
      }
      // Return a 401 response so the caller can handle it
      return new Response(null, { status: 401 });
    },
    [refreshToken, logout],
  );

  // --- devices ---
  const {
    state: devicesState,
    refetch: refetchDevices,
  } = useDomainFetch<Device[]>({
    endpoint: `${API_BASE_URL}/api/devices?includeMetrics=true`,
    refreshInterval: settings.refreshIntervals.devices,
    getToken,
    onUnauthorized,
  });

  // --- remoteIt (null endpoint skips fetch when feature is disabled) ---
  const remoteItEndpoint = process.env.NEXT_PUBLIC_REMOTEIT_ENABLED !== 'false'
    ? `${API_BASE_URL}/api/remoteit/status`
    : null;
  const {
    state: remoteItState,
    refetch: refetchRemoteIt,
  } = useDomainFetch<RemoteItStatus>({
    endpoint: remoteItEndpoint,
    refreshInterval: settings.refreshIntervals.status,
    getToken,
    onUnauthorized,
  });

  // --- pendingReviewDevices ---
  const {
    state: pendingReviewState,
    refetch: refetchPendingReview,
  } = useDomainFetch<PendingReviewDevice[]>({
    endpoint: `${API_BASE_URL}/api/discovery/pending-review`,
    refreshInterval: settings.refreshIntervals.discovery,
    getToken,
    onUnauthorized,
  });

  // Initial fetch on mount for stale sub-domains
  useEffect(() => {
    if (devicesState.isStale || !devicesState.lastFetch) {
      refetchDevices();
    }
    if (remoteItState.isStale || !remoteItState.lastFetch) {
      refetchRemoteIt();
    }
    if (pendingReviewState.isStale || !pendingReviewState.lastFetch) {
      refetchPendingReview();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Re-fetch when marked stale by the auto-refresh interval
  // (matches TelemetryContext pattern -- without these effects, refreshInterval
  // only flips isStale=true and never triggers an actual refetch)
  useEffect(() => {
    if (devicesState.isStale && devicesState.lastFetch) {
      refetchDevices();
    }
  }, [devicesState.isStale, devicesState.lastFetch, refetchDevices]);

  useEffect(() => {
    if (remoteItState.isStale && remoteItState.lastFetch) {
      refetchRemoteIt();
    }
  }, [remoteItState.isStale, remoteItState.lastFetch, refetchRemoteIt]);

  useEffect(() => {
    if (pendingReviewState.isStale && pendingReviewState.lastFetch) {
      refetchPendingReview();
    }
  }, [pendingReviewState.isStale, pendingReviewState.lastFetch, refetchPendingReview]);

  const refetch = useCallback(async (force = false): Promise<void> => {
    await Promise.all([
      refetchDevices(force),
      refetchRemoteIt(force),
      refetchPendingReview(force),
    ]);
  }, [refetchDevices, refetchRemoteIt, refetchPendingReview]);

  // Authenticated fetch helper for action calls (discovery, status, etc.)
  const authenticatedFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const makeRequest = async (): Promise<Response> => {
        const token = getToken();
        return fetch(url, {
          ...options,
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
            ...options.headers,
          },
        });
      };

      let response = await makeRequest();
      if (response.status === 401) {
        response = await onUnauthorized(makeRequest);
      }
      return response;
    },
    [getToken, onUnauthorized],
  );

  // Domain utilities - memoised for stable references

  const getDeviceById = useCallback((deviceId: string): Device | null => {
    if (!devicesState.data) return null;
    return devicesState.data.find((d) => d.deviceId === deviceId) || null;
  }, [devicesState.data]);

  const getDevicesByFarm = useCallback((farmName: string): Device[] => {
    if (!devicesState.data) return [];
    return devicesState.data.filter((d) =>
      d.farmName?.toLowerCase().includes(farmName.toLowerCase()),
    );
  }, [devicesState.data]);

  const getActiveDevices = useCallback((): Device[] => {
    if (!devicesState.data) return [];
    return devicesState.data.filter((d) => d.isActive);
  }, [devicesState.data]);

  const getInactiveDevices = useCallback((): Device[] => {
    if (!devicesState.data) return [];
    return devicesState.data.filter((d) => !d.isActive);
  }, [devicesState.data]);

  const getExperimentalDevices = useCallback((): Device[] => {
    if (!devicesState.data) return [];
    return devicesState.data.filter((d) => {
      if (!isModberryDevice(d)) return false;
      const deviceName = (d.name || d.deviceId || '').toLowerCase();
      const farmName = (d.farmName || '').toLowerCase();
      const experimentalKeywords = ['experimental', 'test', 'dev', 'debug', 'lab', 'staging', 'demo', 'wur', 'dc 2024', 'wateronly'];
      return experimentalKeywords.some((kw) => deviceName.includes(kw) || farmName.includes(kw));
    });
  }, [devicesState.data]);

  const getDevicesNeedingAttention = useCallback((): Device[] => {
    if (!devicesState.data) return [];
    const experimentalIds = new Set(getExperimentalDevices().map((d) => d.deviceId));
    return devicesState.data.filter((d) => {
      if (!isModberryDevice(d)) return false;
      if (EXCLUDED_DEVICE_IDS.has(d.deviceId)) return false;
      if (experimentalIds.has(d.deviceId)) return false;
      // Uncommissioned devices are not yet operational — skip them
      if (!d.commissionedAt) return false;
      const isDemoDevice =
        /\b(beurs|demo)\b/i.test(d.name || '') || /\b(beurs|demo)\b/i.test(d.farmName || '');
      if (isDemoDevice) return false;
      if (d.isProduction === false) return false;
      const hasLowTelemetryScore = d.telemetryHealthScore && d.telemetryHealthScore < 70;
      const isInactive = !d.isActive;
      const hasAlarms = d.activeAlarms && d.activeAlarms.length > 0;
      return hasLowTelemetryScore || isInactive || hasAlarms;
    });
  }, [devicesState.data, getExperimentalDevices]);

  const getProductionDevices = useCallback((): Device[] => {
    if (!devicesState.data) return [];
    const experimentalIds = new Set(getExperimentalDevices().map((d) => d.deviceId));
    return devicesState.data.filter((d) => {
      if (!isModberryDevice(d)) return false;
      if (EXCLUDED_DEVICE_IDS.has(d.deviceId)) return false;
      const isDemoDevice =
        /\b(beurs|demo)\b/i.test(d.name || '') || /\b(beurs|demo)\b/i.test(d.farmName || '');
      if (isDemoDevice) return false;
      return !experimentalIds.has(d.deviceId) && d.isProduction !== false;
    });
  }, [devicesState.data, getExperimentalDevices]);

  const getFleetStatistics = useCallback(() => {
    if (!devicesState.data) {
      return { total: 0, active: 0, inactive: 0, needingAttention: 0, averageHealthScore: 0 };
    }
    const active = getActiveDevices();
    const inactive = getInactiveDevices();
    const needingAttention = getDevicesNeedingAttention();
    const healthScores = devicesState.data
      .map((d) => d.telemetryHealthScore)
      .filter((s): s is number => typeof s === 'number' && s > 0);
    const averageHealthScore =
      healthScores.length > 0
        ? Math.round(healthScores.reduce((sum, s) => sum + s, 0) / healthScores.length)
        : 0;
    return {
      total: devicesState.data.length,
      active: active.length,
      inactive: inactive.length,
      needingAttention: needingAttention.length,
      averageHealthScore,
    };
  }, [devicesState.data, getActiveDevices, getInactiveDevices, getDevicesNeedingAttention]);

  const getDevicesByHealthScore = useCallback((minScore: number): Device[] => {
    if (!devicesState.data) return [];
    return devicesState.data.filter((d) => d.telemetryHealthScore && d.telemetryHealthScore >= minScore);
  }, [devicesState.data]);

  const triggerDiscovery = useCallback(async (): Promise<boolean> => {
    const startTime = Date.now();
    try {
      logger.info('Triggering device discovery', 'DeviceContext');
      const response = await authenticatedFetch(`${API_BASE_URL}/api/discovery/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const duration = Date.now() - startTime;
      logger.logNetworkActivity('POST', '/api/discovery/scan', response.status, duration);
      if (response.ok) {
        await refetch(true);
        return true;
      }
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error(`Device discovery failed: ${errorMessage}`, 'DeviceContext');
      return false;
    }
  }, [authenticatedFetch, refetch]);

  const forceRefreshDevice = useCallback(async (deviceId: string): Promise<boolean> => {
    const startTime = Date.now();
    try {
      logger.info(`Force refreshing device ${deviceId}`, 'DeviceContext');
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/api/devices?deviceId=${encodeURIComponent(deviceId)}&forceRefresh=true&includeMetrics=true`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        },
      );
      const duration = Date.now() - startTime;
      logger.logNetworkActivity('GET', `/api/devices?deviceId=${deviceId}&forceRefresh=true`, response.status, duration);
      if (response.ok) {
        await refetchDevices(true);
        return true;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error(`Device ${deviceId} force refresh failed: ${errorMessage}`, 'DeviceContext');
      return false;
    }
  }, [getToken, refetchDevices]);

  const fetchDeviceStatus = useCallback(async (deviceId: string): Promise<DeviceStatus> => {
    const startTime = Date.now();
    try {
      logger.info(`Fetching status for device ${deviceId}`, 'DeviceContext');
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/api/devices/${encodeURIComponent(deviceId)}/status`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        },
      );
      const duration = Date.now() - startTime;
      logger.logNetworkActivity('GET', `/api/devices/${deviceId}/status`, response.status, duration);
      if (response.ok) {
        return response.json() as Promise<DeviceStatus>;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error(`Device ${deviceId} status fetch failed: ${errorMessage}`, 'DeviceContext');
      throw error;
    }
  }, [getToken]);

  const fetchFlowDegradationTimeline = useCallback(
    async (deviceId: string): Promise<FlowDegradationTimeline | null> => {
      const startTime = Date.now();
      try {
        logger.info(`Fetching flow degradation timeline for device ${deviceId}`, 'DeviceContext');
        const response = await authenticatedFetch(
          `${API_BASE_URL}/api/devices/${encodeURIComponent(deviceId)}/flow-degradation`,
          { method: 'GET', headers: { 'Content-Type': 'application/json' } },
        );
        const duration = Date.now() - startTime;
        logger.logNetworkActivity('GET', `/api/devices/${deviceId}/flow-degradation`, response.status, duration);
        if (response.ok) {
          return response.json() as Promise<FlowDegradationTimeline>;
        }
        if (response.status === 503) {
          logger.warn(`Flow degradation analysis not available for device ${deviceId}`, 'DeviceContext');
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error(`Device ${deviceId} flow degradation timeline fetch failed: ${errorMessage}`, 'DeviceContext');
        return null;
      }
    },
    [authenticatedFetch],
  );

  // Aggregate loading/error state
  // Only devices and pendingReview are critical for the main view.
  // remoteIt errors (e.g. REMOTEIT_ENABLED=false → 400) should not block device rendering.
  const loading = devicesState.loading || pendingReviewState.loading;
  const error = devicesState.error || pendingReviewState.error;
  const isStale = devicesState.isStale || remoteItState.isStale || pendingReviewState.isStale;
  const lastFetch = useMemo(() => {
    const fetches = [devicesState.lastFetch, remoteItState.lastFetch, pendingReviewState.lastFetch]
      .filter((f): f is number => f !== null);
    return fetches.length > 0 ? Math.min(...fetches) : null;
  }, [devicesState.lastFetch, remoteItState.lastFetch, pendingReviewState.lastFetch]);

  const contextValue: DeviceContextType = {
    devices: devicesState.data,
    remoteIt: remoteItState.data,
    pendingReviewDevices: pendingReviewState.data,
    loading,
    error,
    isStale,
    lastFetch,
    refetch,
    getDeviceById,
    getDevicesByFarm,
    getActiveDevices,
    getInactiveDevices,
    getDevicesNeedingAttention,
    getExperimentalDevices,
    getProductionDevices,
    getFleetStatistics,
    getDevicesByHealthScore,
    triggerDiscovery,
    forceRefreshDevice,
    fetchDeviceStatus,
    fetchFlowDegradationTimeline,
  };

  return (
    <DeviceContext.Provider value={contextValue}>
      {children}
    </DeviceContext.Provider>
  );
}

export function useDeviceContext(): DeviceContextType {
  const context = useContext(DeviceContext);
  if (context === undefined) {
    throw new Error('useDeviceContext must be used within a DeviceProvider');
  }
  return context;
}

// ---- Correlation helpers (pure utility functions) ----
// These were previously in DataManagerContext.tsx. Moved here since they
// primarily operate on device data and are used by hooks consuming DeviceContext.

export interface DeviceCorrelationMap {
  farmHash: string;
  flowDeviceId: string | null;
  nh3DeviceIds: string[];
  barnThermometerDeviceIds: string[];
  farmName: string;
}

export interface CorrelatedTelemetryData {
  deviceId: string;
  farmHash: string;
  farmName: string;
  timeSeries: Array<{
    timestamp: string;
    dosingFlowRate?: number;
    waterToMixtankFlowRate?: number;
    liquidToMixtankFlowRate?: number;
    nh3Level?: number;
    dosingEfficiency?: number;
    tankLevel?: number;
  }>;
}

export function _correlateDevicesByFarmHash(devices: Device[]): DeviceCorrelationMap[] {
  const correlationMap: DeviceCorrelationMap[] = [];
  const farmHashMap = new Map<string, {
    flowDeviceId: string | null;
    nh3DeviceIds: string[];
    barnThermometerDeviceIds: string[];
    farmName: string;
  }>();

  const devicesToCorrelate = devices.filter(shouldCorrelateDevice);

  devicesToCorrelate.forEach((device) => {
    let farmHash = extractDeviceUUID(device.farmName || '');
    if (!farmHash && device.name) {
      farmHash = extractDeviceUUID(device.name);
    }
    if (!farmHash) return;

    if (!farmHashMap.has(farmHash)) {
      farmHashMap.set(farmHash, {
        flowDeviceId: null,
        nh3DeviceIds: [],
        barnThermometerDeviceIds: [],
        farmName: device.farmName || '',
      });
    }

    const entry = farmHashMap.get(farmHash)!;
    const isNH3 = isNH3Sensor(device);
    const isBarnTherm = isBarnThermometer(device);
    const isCellar = isCellarDepthLaser(device);
    const isModberry = isModberryDevice(device);

    if (isNH3) {
      entry.nh3DeviceIds.push(device.deviceId);
    } else if (isBarnTherm) {
      entry.barnThermometerDeviceIds.push(device.deviceId);
    } else if (isModberry) {
      entry.flowDeviceId = device.deviceId;
    } else if (isCellar) {
      if (!entry.flowDeviceId) {
        entry.flowDeviceId = device.deviceId;
      }
    } else {
      if (!entry.flowDeviceId) {
        entry.flowDeviceId = device.deviceId;
      }
    }
  });

  farmHashMap.forEach((entry, farmHash) => {
    correlationMap.push({
      farmHash,
      flowDeviceId: entry.flowDeviceId,
      nh3DeviceIds: entry.nh3DeviceIds,
      barnThermometerDeviceIds: entry.barnThermometerDeviceIds,
      farmName: entry.farmName,
    });
  });

  return correlationMap;
}

export function _mergeNH3WithFlowTelemetry(
  flowData: FlowTelemetryResponse[],
  nh3Data: NH3TelemetryResponse[],
  correlationMap: DeviceCorrelationMap[],
): CorrelatedTelemetryData[] {
  const correlatedData: CorrelatedTelemetryData[] = [];

  flowData.forEach((flowDeviceData) => {
    const flowDeviceId = flowDeviceData.deviceId;
    const correlation = correlationMap.find((c) => c.flowDeviceId === flowDeviceId);

    if (!correlation) {
      correlatedData.push({
        deviceId: flowDeviceId,
        farmHash: '',
        farmName: flowDeviceData.deviceName || flowDeviceData.farmName || '',
        timeSeries: (flowDeviceData.timeSeries || []).map((p) => ({
          timestamp: p.timestamp,
          dosingFlowRate: p.dosingFlowRate || undefined,
          waterToMixtankFlowRate: p.waterToMixtankFlowRate || undefined,
          liquidToMixtankFlowRate: p.liquidToMixtankFlowRate || undefined,
          dosingEfficiency: p.dosingEfficiency || undefined,
        })),
      });
      return;
    }

    // Build sorted NH3 entries for nearest-timestamp matching.
    // Flow and NH3 sensors report at different intervals, so exact timestamp
    // matching never works. Instead we match to the nearest NH3 reading
    // within a 30-minute window (half a typical bucket at maxPoints=150 over 72h).
    const NH3_MATCH_WINDOW_MS = 30 * 60 * 1000;
    const nh3Entries: { ts: number; value: number }[] = [];
    nh3Data
      .filter((nh3Device) => correlation.nh3DeviceIds.includes(nh3Device.deviceId))
      .forEach((nh3Device) => {
        (nh3Device.timeSeries || []).forEach((p) => {
          if (p.nh3Level !== undefined && p.nh3Level !== null) {
            nh3Entries.push({ ts: new Date(p.timestamp).getTime(), value: p.nh3Level });
          }
        });
      });
    nh3Entries.sort((a, b) => a.ts - b.ts);

    const findNearestNH3 = (timestamp: string): number | undefined => {
      if (nh3Entries.length === 0) return undefined;
      const targetTs = new Date(timestamp).getTime();
      // Binary search for nearest entry
      let lo = 0;
      let hi = nh3Entries.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (nh3Entries[mid].ts < targetTs) lo = mid + 1;
        else hi = mid;
      }
      // Check lo and lo-1 for nearest
      let bestIdx = lo;
      if (lo > 0 && Math.abs(nh3Entries[lo - 1].ts - targetTs) < Math.abs(nh3Entries[lo].ts - targetTs)) {
        bestIdx = lo - 1;
      }
      const diff = Math.abs(nh3Entries[bestIdx].ts - targetTs);
      return diff <= NH3_MATCH_WINDOW_MS ? nh3Entries[bestIdx].value : undefined;
    };

    correlatedData.push({
      deviceId: flowDeviceId,
      farmHash: correlation.farmHash,
      farmName: correlation.farmName,
      timeSeries: (flowDeviceData.timeSeries || []).map((p) => ({
        timestamp: p.timestamp,
        dosingFlowRate: p.dosingFlowRate || undefined,
        waterToMixtankFlowRate: p.waterToMixtankFlowRate || undefined,
        liquidToMixtankFlowRate: p.liquidToMixtankFlowRate || undefined,
        dosingEfficiency: p.dosingEfficiency || undefined,
        nh3Level: findNearestNH3(p.timestamp),
      })),
    });
  });

  return correlatedData;
}

export default DeviceProvider;
